import { useState } from "react"

import { ScoreMovementMark } from "@/components/terminal/band-mark"
import {
  changeDate,
  ChangeDescription,
  NO_ACTIVITY,
} from "@/components/terminal/change-description"
import { formatTerminalDate } from "@/lib/terminal/date"
import type {
  TerminalDetectionView,
  TerminalHistoryAssessment,
} from "@/lib/terminal/file-view"
import { scoreChanges } from "@/lib/terminal/score-movement"
import { cn } from "@/lib/utils"

import { FileBox } from "./file-section"

const SHOWN_CHANGE_COUNT = 3

// A monitored Company's counted score changes, newest first: the movement
// mark and the date, then what happened. The latest three show until the
// reader expands the rest in place.
export const ActivityBox = ({
  history,
  detections,
}: {
  history: TerminalHistoryAssessment[]
  detections: TerminalDetectionView[]
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const changes = scoreChanges(history)
  const shownChanges = isExpanded
    ? changes
    : changes.slice(0, SHOWN_CHANGE_COUNT)
  return (
    <FileBox label="Reassessments">
      {changes.length === 0 && (
        <div className="file-row items-center">
          <span className="type-row-label text-muted-foreground">
            {NO_ACTIVITY}
          </span>
        </div>
      )}
      <ol>
        {shownChanges.map((change, index) => (
          <li
            key={change.assessedAt}
            className={cn(index > 0 && "file-row-rule")}
          >
            {/* The entry reads like every other box row (Dhanur, 24 Oct,
                layout B): the date in the label slot, the move in the value
                slot; then its lines as the subordinate group beneath, the
                way a metric's benchmarks sit under it. */}
            <div className="file-row items-center justify-between gap-3">
              <span className="type-row-label text-foreground">
                {formatTerminalDate(changeDate(change, detections))}
              </span>
              <ScoreMovementMark
                band={change.band}
                score={change.score}
                movement={change.movement}
              />
            </div>
            <ChangeDescription
              movement={change.movement}
              detections={detections}
              className="pb-[var(--file-benchmark-group-padding-bottom)]"
            />
          </li>
        ))}
      </ol>
      {changes.length > SHOWN_CHANGE_COUNT && (
        <button
          type="button"
          onClick={() =>
            setIsExpanded((isCurrentlyExpanded) => !isCurrentlyExpanded)
          }
          className="file-row file-row-rule type-row-label text-muted-foreground hover:text-foreground w-full cursor-pointer items-center border-0 bg-transparent px-0 text-left"
        >
          {isExpanded ? "Show fewer" : `Show all ${changes.length}`}
        </button>
      )}
    </FileBox>
  )
}
